import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const EducationFund = () => {
  // State variables for Child Education Fund calculation
  const [currentCost, setCurrentCost] = useState(0); // Current cost of education
  const [inflationRate, setInflationRate] = useState(0); // Annual inflation rate
  const [years, setYears] = useState(0); // Number of years until education
  const [futureCost, setFutureCost] = useState(0); // Calculated future cost

  // Event handlers to update state values
  const handleCurrentCostChange = (e) =>
    setCurrentCost(parseFloat(e.target.value) || 0);
  const handleInflationRateChange = (e) =>
    setInflationRate(parseFloat(e.target.value) || 0);
  const handleYearsChange = (e) => setYears(parseInt(e.target.value) || 0);

  // useEffect hook to calculate the Future Cost of Education
  useEffect(() => {
    if (currentCost > 0 && inflationRate >= 0 && years > 0) {
      // Future Cost = Current Cost * (1 + Inflation Rate) ^ Years
      const futureCostCalc =
        currentCost * Math.pow(1 + inflationRate / 100, years);
      setFutureCost(futureCostCalc);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [currentCost, inflationRate, years]);

  return (
    <Layout title="Child Education Fund Calculator" path="child-education-fund">
      <section className="section-padding" id="education-fund">
        <div className="container mt-5">
          <div className="row">
            {/* Child Education Fund Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Child Education Fund Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Current Cost */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="currentCost" className="form-label">
                        Current Cost of Education
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Current Cost of Education:</strong> The current expense for the desired education program."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="currentCost"
                        placeholder="Enter current education cost"
                        value={currentCost || ""}
                        onChange={handleCurrentCostChange}
                      />
                    </div>

                    {/* Input for Inflation Rate */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="inflationRate" className="form-label">
                        Annual Inflation Rate (%)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Annual Inflation Rate:</strong> The expected annual percentage increase in education costs."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="inflationRate"
                        placeholder="Enter inflation rate"
                        value={inflationRate || ""}
                        onChange={handleInflationRateChange}
                      />
                    </div>

                    {/* Input for Number of Years */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="years" className="form-label">
                        Number of Years
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Number of Years:</strong> The number of years until the child begins the education program."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="years"
                        placeholder="Enter number of years until education"
                        value={years || ""}
                        onChange={handleYearsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Future Cost of Education:{" "}
                        <span className="fw-bold ms-3">
                          {futureCost === 0
                            ? "0"
                            : futureCost.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Child Education Fund
                </h3>

                {/* What is Child Education Fund? */}
                <h4 className="mt-3 mb-3">What is Child Education Fund?</h4>
                <p>
                  The **Child Education Fund** refers to the amount of money
                  parents need to save for their child's education, considering
                  the effects of inflation over time. This calculator helps you
                  estimate how much you need to set aside to ensure you can
                  cover your child's education costs in the future.
                </p>

                {/* Why Use the Child Education Fund? */}
                <h4 className="mt-4 mb-3">Why Use the Child Education Fund?</h4>
                <p>
                  Understanding how inflation impacts education costs is crucial
                  for setting realistic savings goals. By using this tool,
                  parents can ensure they are saving enough to cover the future
                  cost of education and avoid any financial shortfalls when the
                  time comes.
                </p>

                {/* Benefits of Using the Child Education Fund */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using the Child Education Fund
                </h4>
                <ul>
                  <li>
                    <strong>Helps Plan for Future Education Costs:</strong>{" "}
                    Assists parents in estimating how much they need to save
                    each year.
                  </li>
                  <li>
                    <strong>Helps Set Realistic Saving Goals:</strong> Provides
                    a clear target for how much money should be saved over time.
                  </li>
                  <li>
                    <strong>Supports Long-Term Financial Planning:</strong>{" "}
                    Encourages early planning and consistent saving for the
                    child's future educational expenses.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use the Child Education Fund Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Parents:</strong> Helps parents plan and save for
                    their child's education.
                  </li>
                  <li>
                    <strong>Financial Advisors:</strong> Assists financial
                    planners in advising clients on how much to save for
                    education.
                  </li>
                  <li>
                    <strong>Family Finance Planners:</strong> Supports families
                    in managing their finances for future needs, including
                    education.
                  </li>
                </ul>

                {/* Two Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How does inflation affect the cost of education?
                </h4>
                <p>
                  Inflation increases the cost of goods and services over time,
                  including education. By accounting for inflation, this
                  calculator provides a more accurate estimate of the future
                  cost of education, ensuring that savings goals are realistic
                  and achievable.
                </p>

                <h4 className="mt-4 mb-3">
                  When should I start saving for my child's education?
                </h4>
                <p>
                  It's best to start saving as early as possible, ideally when
                  the child is born, to take advantage of compounding growth.
                  The earlier you start, the less you'll need to save each month
                  to reach your goal.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EducationFund;
